"use client";

import React from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";

/**
 * Composant pour afficher le résumé d'XP d'un ou plusieurs utilisateurs
 * @param {boolean} showDetailedStats - Afficher les statistiques détaillées (moyenne, série, etc.) - défaut: false
 */
export default function XpSummaryCard({
    users = [],
    period = "cette semaine",
    className = "",
    series = [], // Nouveau prop pour les couleurs des séries
    locale = "fr",
    showDetailedStats = false,
}) {
    const t = useTranslations("xpDashboard");
    // Fonction pour obtenir la couleur d'un utilisateur
    const getUserColor = (user, index) => {
        if (user.isCurrentUser) {
            return "#9CA3AF"; // Gris pour l'utilisateur actuel
        }

        // Trouver la série correspondante ou utiliser l'index
        const correspondingSerie = series.find((s) => s.name === user.name);
        if (correspondingSerie) {
            return correspondingSerie.color;
        }

        // Couleurs par défaut si pas de série trouvée
        const modernColors = [
            "#58CC02",
            "#FF9600",
            "#CE82FF",
            "#00B4D8",
            "#FF4757",
        ];
        return modernColors[index % modernColors.length];
    };

    // Calculer les statistiques pour un utilisateur
    const calculateStats = (user) => {
        if (!user.chartData || user.chartData.length === 0) {
            return {
                average: 0,
                bestDay: null,
                bestDayXp: 0,
                streak: 0,
                activeDays: 0,
            };
        }

        const chartData = user.chartData;
        const totalDays = chartData.length;
        const average = totalDays > 0 ? Math.round(user.totalXp / totalDays) : 0;

        // Trouver le meilleur jour
        let bestDay = null;
        let bestDayXp = 0;
        chartData.forEach((day) => {
            if (day.xp > bestDayXp) {
                bestDayXp = day.xp;
                bestDay = day.day;
            }
        });

        // Calculer le streak actuel (jours consécutifs avec XP > 0 depuis la fin)
        let streak = 0;
        for (let i = chartData.length - 1; i >= 0; i--) {
            if (chartData[i].xp > 0) {
                streak++;
            } else {
                break;
            }
        }

        // Compter les jours actifs (XP > 0)
        const activeDays = chartData.filter((day) => day.xp > 0).length;

        return {
            average,
            bestDay,
            bestDayXp,
            streak,
            activeDays,
        };
    };
    return (
        <div className={className}>
            {/* Affichage des utilisateurs */}
            {users.map((user, index) => {
                const userColor = getUserColor(user, index);
                const stats = calculateStats(user);

                return (
                    <div
                        key={user.id}
                        className={`${index > 0 ? "mt-4 sm:mt-6" : ""}`}
                    >
                        {/* En-tête avec nom et total XP */}
                        <div className={`flex items-center gap-2 sm:gap-4 ${users.length === 1 && !showDetailedStats ? "mb-1 sm:mb-2" : users.length === 1 ? "mb-0" : "mb-2 sm:mb-3"}`}>
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div
                                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0 ${
                                        user.isCurrentUser ? "border-2" : ""
                                    }`}
                                    style={{
                                        backgroundColor: user.isCurrentUser
                                            ? "transparent"
                                            : userColor,
                                        borderColor: user.isCurrentUser
                                            ? userColor
                                            : "transparent",
                                    }}
                                ></div>
                                {user.userSlug ? (
                                    <Link
                                        href={`/@${user.userSlug}`}
                                        className="font-medium text-sm sm:text-lg hover:underline truncate"
                                        style={{ color: userColor }}
                                    >
                                        {user.name}
                                    </Link>
                                ) : (
                                    <span
                                        className="font-medium text-sm sm:text-lg truncate"
                                        style={{ color: userColor }}
                                    >
                                        {user.name}
                                    </span>
                                )}
                            </div>
                            <div className="flex-shrink-0">
                                <span
                                    className="font-bold text-base sm:text-xl"
                                    style={{ color: userColor }}
                                >
                                    {user.totalXp.toLocaleString(locale)} XP
                                </span>
                            </div>
                        </div>

                        {/* Statistiques enrichies - Visible uniquement si showDetailedStats est true */}
                        {showDetailedStats && (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mt-2">
                                {/* Moyenne quotidienne */}
                                <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-lg p-2 sm:p-3">
                                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0.5 sm:mb-1">
                                        {t("averagePerDay")}
                                    </div>
                                    <div className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                                        {stats.average.toLocaleString(locale)} XP
                                    </div>
                                </div>

                                {/* Meilleur jour */}
                                <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-lg p-2 sm:p-3">
                                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0.5 sm:mb-1">
                                        {t("bestDay")}
                                    </div>
                                    <div className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                                        {stats.bestDayXp > 0 ? (
                                            <>
                                                {stats.bestDayXp.toLocaleString(locale)} XP
                                                <div className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mt-0.5 truncate">
                                                    {stats.bestDay}
                                                </div>
                                            </>
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                </div>

                                {/* Streak actuel */}
                                <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-lg p-2 sm:p-3">
                                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0.5 sm:mb-1">
                                        {t("currentStreak")}
                                    </div>
                                    <div className="font-bold text-sm sm:text-base text-gray-900 dark:text-white flex items-center gap-1">
                                        {stats.streak > 0 ? (
                                            <>
                                                <span className="text-orange-500">🔥</span>
                                                {stats.streak} {t("day", { count: stats.streak })}
                                            </>
                                        ) : (
                                            `0 ${t("day", { count: 0 })}`
                                        )}
                                    </div>
                                </div>

                                {/* Jours actifs */}
                                <div className="bg-gray-50 dark:bg-grayDark-700/50 rounded-lg p-2 sm:p-3">
                                    <div className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mb-0.5 sm:mb-1">
                                        {t("activeDays")}
                                    </div>
                                    <div className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                                        {stats.activeDays}/{user.chartData?.length || 0}
                                        <div className="text-[10px] sm:text-xs text-gray-500 dark:text-grayDark-400 mt-0.5">
                                            {user.chartData?.length > 0
                                                ? Math.round((stats.activeDays / user.chartData.length) * 100)
                                                : 0}
                                            %
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Message si aucun utilisateur */}
            {users.length === 0 && (
                <div className="text-gray-500 dark:text-grayDark-400 text-center py-3 sm:py-4 text-sm">
                    {t("noDataAvailable")}
                </div>
            )}
        </div>
    );
}
